import { createTheme, alpha, Shadows } from '@mui/material/styles';

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    highlighted: true;
  }
}
declare module '@mui/material/styles' {
  interface ColorRange {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  }

  interface PaletteColor extends ColorRange { }

  interface Palette {
    baseShadow: string;
  }
}

const defaultTheme = createTheme();

export const brand = {
  50: '#FFF9ED',
  100: '#F8EDD6',
  200: '#EBD4A8',
  300: '#DCBD7F',
  400: '#C9A15A',
  500: '#B38A43',
  600: '#8E6A2F',
  700: '#684B20',
  800: '#3F2E16',
  900: '#22180B',
};

export const teal = {
  50: '#E8FBFA',
  100: '#C8F2F0',
  200: '#91DEDD',
  300: '#56C9C8',
  400: '#0FA5A5',
  500: '#0D8E90',
  600: '#0A6F73',
  700: '#075559',
  800: '#053C41',
  900: '#03262A',
};

export const gray = {
  50: '#F6F8F9',
  100: '#E8ECEF',
  200: '#D4DBE0',
  300: '#B8C2CA',
  400: '#88929B',
  500: '#66727C',
  600: '#49545D',
  700: '#2F3942',
  800: '#0B1218',
  900: '#060B10',
};

export const green = {
  50: 'hsl(120, 80%, 98%)',
  100: 'hsl(120, 75%, 94%)',
  200: 'hsl(120, 75%, 87%)',
  300: 'hsl(120, 61%, 77%)',
  400: 'hsl(120, 44%, 53%)',
  500: 'hsl(120, 59%, 30%)',
  600: 'hsl(120, 70%, 25%)',
  700: 'hsl(120, 75%, 16%)',
  800: 'hsl(120, 84%, 10%)',
  900: 'hsl(120, 87%, 6%)',
};

export const orange = {
  50: 'hsl(45, 100%, 97%)',
  100: 'hsl(45, 92%, 90%)',
  200: 'hsl(45, 94%, 80%)',
  300: 'hsl(45, 90%, 65%)',
  400: 'hsl(45, 90%, 40%)',
  500: 'hsl(45, 90%, 35%)',
  600: 'hsl(45, 91%, 25%)',
  700: 'hsl(45, 94%, 20%)',
  800: 'hsl(45, 95%, 16%)',
  900: 'hsl(45, 93%, 12%)',
};

export const red = {
  50: 'hsl(0, 100%, 97%)',
  100: 'hsl(0, 92%, 90%)',
  200: 'hsl(0, 94%, 80%)',
  300: 'hsl(0, 90%, 65%)',
  400: 'hsl(0, 90%, 40%)',
  500: 'hsl(0, 90%, 30%)',
  600: 'hsl(0, 91%, 25%)',
  700: 'hsl(0, 94%, 18%)',
  800: 'hsl(0, 95%, 12%)',
  900: 'hsl(0, 93%, 6%)',
};

export const colorSchemes = {
  light: {
    palette: {
      primary: {
        light: brand[300],
        main: brand[500],
        dark: brand[700],
        contrastText: '#060B10',
      },
      secondary: {
        light: teal[300],
        main: teal[500],
        dark: teal[700],
        contrastText: '#FFFFFF',
      },
      info: {
        light: teal[200],
        main: teal[400],
        dark: teal[700],
        contrastText: '#FFFFFF',
      },
      warning: {
        light: orange[300],
        main: orange[400],
        dark: orange[800],
      },
      error: {
        light: red[300],
        main: red[400],
        dark: red[800],
      },
      success: {
        light: green[300],
        main: green[400],
        dark: green[800],
      },
      grey: {
        ...gray,
      },
      divider: alpha(gray[300], 0.7),
      background: {
        default: '#F6F8F9',
        paper: '#FFFFFF',
        bgLinear: `linear-gradient(180deg, ${gray[50]} 0%, #FFFFFF 100%)`,
      },
      text: {
        primary: gray[900],
        secondary: gray[500],
        warning: orange[400],
      },
      action: {
        hover: alpha(teal[100], 0.5),
        // selected: alpha(brand[100], 0.5),
        selectedOpacity: 0.5,
        hoverOpacity: 0.15,
      },
      DataGrid: {
        headerBg: gray[100],
        bg: 'transparent',
      },
      baseShadow:
        'rgba(6, 11, 16, 0.08) 0px 12px 32px -12px, rgba(6, 11, 16, 0.05) 0px 4px 12px -6px',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: '#060B10',
        light: brand[200],
        main: brand[400],
        dark: brand[600],
      },
      secondary: {
        contrastText: '#E8ECEF',
        light: teal[300],
        main: teal[400],
        dark: teal[700],
      },
      info: {
        contrastText: '#E8ECEF',
        light: teal[300],
        main: teal[500],
        dark: teal[800],
      },
      warning: {
        light: orange[400],
        main: orange[500],
        dark: orange[700],
      },
      error: {
        light: red[400],
        main: red[500],
        dark: red[700],
      },
      success: {
        light: green[400],
        main: green[500],
        dark: green[700],
      },
      grey: {
        ...gray,
      },
      divider: alpha(gray[700], 0.6),
      background: {
        default: gray[900],
        paper: gray[800],
        bgLinear: `linear-gradient(180deg, ${gray[900]} 0%, ${gray[800]} 100%)`,
      },
      text: {
        primary: gray[100],
        secondary: gray[400],
        warning: brand[400],
      },
      action: {
        hover: alpha(teal[500], 0.18),
        selected: alpha(brand[500], 0.24),
      },
      DataGrid: {
        headerBg: gray[800],
        bg: 'transparent',
      },
      baseShadow:
        'rgba(0, 0, 0, 0.42) 0px 18px 70px -24px, rgba(0, 0, 0, 0.36) 0px 8px 24px -16px',
    },
  },
};

export const typography = {
  fontFamily: 'Inter, sans-serif',
  h1: {
    fontSize: defaultTheme.typography.pxToRem(48),
    fontWeight: 700,
    lineHeight: 1.2,
  },
  h2: {
    fontSize: defaultTheme.typography.pxToRem(36),
    fontWeight: 700,
    lineHeight: 1.2,
  },
  h3: {
    fontSize: defaultTheme.typography.pxToRem(30),
    lineHeight: 1.2,
  },
  h4: {
    fontSize: defaultTheme.typography.pxToRem(24),
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h5: {
    fontSize: defaultTheme.typography.pxToRem(20),
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h6: {
    fontSize: defaultTheme.typography.pxToRem(18),
    fontWeight: 600,
    lineHeight: 1.4,
  },
  subtitle1: {
    fontSize: defaultTheme.typography.pxToRem(16),
    fontWeight: 500,
    lineHeight: 1.5,
  },
  subtitle2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 500,
    lineHeight: 1.5,
  },
  body1: {
    fontSize: defaultTheme.typography.pxToRem(16),
    fontWeight: 400,
    lineHeight: 1.5,
  },
  body2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 400,
    lineHeight: 1.5,
  },
  caption: {
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 400,
  },
};

export const shape = {
  borderRadius: 8,
};

const defaultShadows: Shadows = [...defaultTheme.shadows];
defaultShadows[1] = 'var(--t-palette-baseShadow)';
defaultShadows[2] = 'var(--t-palette-baseShadow)';
export const shadows = defaultShadows;
